import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { UiValues } from './_shared';

/**
 * Global keyboard shortcuts for the calculator. Mirrors the operator-tool
 * pattern from Linear / Stripe — single-key triggers fired only when no
 * input is focused, so typing into a field doesn't accidentally toggle
 * a location.
 *
 *   1   toggle Front
 *   2   toggle Back
 *   3   toggle Sleeve
 *   b   cycle pricing mode (B2C ↔ B2B)
 *   r   cycle rush (none → rush → hot_rush → none)
 *
 * Cmd/Ctrl-K stays owned by the parse textbox.
 */
export function useCalcShortcuts(form: UseFormReturn<UiValues>): void {
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isFormElementFocused()) return;

      const key = e.key.toLowerCase();
      const v = form.getValues();

      if (key === '1') {
        e.preventDefault();
        form.setValue('frontEnabled', !v.frontEnabled, { shouldDirty: true });
        return;
      }
      if (key === '2') {
        e.preventDefault();
        form.setValue('backEnabled', !v.backEnabled, { shouldDirty: true });
        return;
      }
      if (key === '3') {
        e.preventDefault();
        form.setValue('sleeveEnabled', !v.sleeveEnabled, { shouldDirty: true });
        return;
      }
      if (key === 'b') {
        e.preventDefault();
        form.setValue('mode', v.mode === 'b2c' ? 'b2b' : 'b2c', { shouldDirty: true });
        return;
      }
      if (key === 'r') {
        e.preventDefault();
        const next: UiValues['rush'] =
          v.rush === 'none' ? 'rush' : v.rush === 'rush' ? 'hot_rush' : 'none';
        form.setValue('rush', next, { shouldDirty: true });
        return;
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [form]);
}

function isFormElementFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}
